import Component from '@glimmer/component'
import { service } from '@ember/service'
import { use } from 'ember-resources'
import { eq } from 'ember-truth-helpers'
import { t } from 'ember-intl'
import { keepLatest } from 'reactiveweb/keep-latest'
import { trackedFunction } from 'reactiveweb/function'
import type ReportingUiScopeRoute from 'teacher-ui-client/reporting/ui-scope/route'
import { toRequestScope } from 'teacher-ui-client/services/reporting'
import { ReflectionCounter } from 'teacher-ui-client/components/writing-legends/reflection-counter'
import { RecentWritingCardEWIAndLongWrite } from 'teacher-ui-client/components/writing-legends/recent-writing-card-ewi-long-write'
import type RecentWritingService from 'teacher-ui-client/services/recent-writing'
import { joinQueryParams } from 'teacher-ui-client/utils/uri'
import FidgetSpinnerWaveComponent from '@blakeelearning/fidget/components/fidget/spinner/wave'
import BasePageSelector from 'teacher-ui-client/components/base/page-selector'

interface RecentWritingItem {
  student_id: string
  canonical_student_id: number
  first_name: string
  last_name: string
  avatar?: string
  classroom_id: string
  classroom_name: string
  activity_id: string
  activity_result_id: string
  latest_result_snapshot_id: string
  activity_result_status: string
  snapshot_updated_date: string
  activity_answer_id: string
  activity_title: string
  reflection: string
  answer_plaintext: string
  stats_auto_feedback_score: number
  stats_word_count: number
  activity_feedback_id?: string
  user_target_word_count?: number
}

interface RecentWritingData {
  data: {
    classroom_id: string
    reflectionSmileCount: number
    reflectionUnsureCount: number
    reflectionDisappointedCount: number
    recent_writing: RecentWritingItem[]
    results_total: number
  }
}

interface Signature {
  Args: {
    uiScope: ModelFor<ReportingUiScopeRoute>['uiScope']
    period: ModelFor<ReportingUiScopeRoute>['period']
    pageNumber: number
    perPage: number
    type?: string
  }
}

export class RecentWritingsListEWIAndLongWrite extends Component<Signature> {
  @service recentWriting!: RecentWritingService

  fetchData = trackedFunction(this, () => {
    const requestScope = toRequestScope(this.args.uiScope)
    const queryPeriod = this.recentWriting.buildQueryPeriod(this.args.period)

    const url = this.recentWriting.buildUrl('ewi_recent_writing')
    const joinedUrl = joinQueryParams(url, {
      fromDate: queryPeriod.start,
      toDate: queryPeriod.end,
      take: this.args.perPage,
      skip: (this.args.pageNumber - 1) * this.args.perPage,
      clId: requestScope.scope === 'school-class' ? requestScope.id : undefined,
      tId: requestScope.scope === 'teacher' ? requestScope.id : undefined,
      template: this.args.type,
    })

    return this.recentWriting.fetchData<RecentWritingData>(joinedUrl.href)
  })

  @use
  latestData = keepLatest({
    value: () => this.fetchData.value,
    when: () => this.fetchData.isPending,
  })

  get recentWritings() {
    return this.latestData?.data.recent_writing
  }

  get totalPages() {
    return Math.ceil((this.latestData?.data?.results_total ?? 0) / this.args.perPage)
  }

  get reflectionCounts() {
    return {
      smile: this.latestData?.data.reflectionSmileCount ?? 0,
      unsure: this.latestData?.data.reflectionUnsureCount ?? 0,
      disappointed: this.latestData?.data.reflectionDisappointedCount ?? 0,
    }
  }

  <template>
    {{#if this.fetchData.isPending}}
      <FidgetSpinnerWaveComponent @centered={{true}} />
    {{else}}
      {{#if (eq this.recentWritings.length 0)}}
        <span class="text-center italic">{{t "pages.recentWriting.noDataAvailable"}}</span>
      {{else}}
        <div class="mb-3 flex justify-end">
          <ReflectionCounter
            @smile={{this.reflectionCounts.smile}}
            @unsure={{this.reflectionCounts.unsure}}
            @disappointed={{this.reflectionCounts.disappointed}}
          />
        </div>

        <ul class="space-y-3">
          {{#each this.recentWritings as |writing|}}
            <RecentWritingCardEWIAndLongWrite
              @type={{@type}}
              @studentId={{writing.student_id}}
              @firstName={{writing.first_name}}
              @lastName={{writing.last_name}}
              @avatar={{writing.avatar}}
              @className={{writing.classroom_name}}
              @statsWordCount={{writing.stats_word_count}}
              @reflection={{writing.reflection}}
              @latestResultSnapshotId={{writing.latest_result_snapshot_id}}
              @snapshotUpdatedDate={{writing.snapshot_updated_date}}
              @activityFeedbackId={{writing.activity_feedback_id}}
              @statsAutoFeedbackScore={{writing.stats_auto_feedback_score}}
              @activityId={{writing.activity_id}}
              @activityTitle={{writing.activity_title}}
              @answerPlainText={{writing.answer_plaintext}}
              @userTargetWordCount={{writing.user_target_word_count}}
            />
          {{/each}}
        </ul>

        <BasePageSelector
          class="mt-8"
          @pageCount={{this.totalPages}}
          @itemCount={{this.latestData.data.results_total}}
          @itemsPerPage={{@perPage}}
        />
      {{/if}}
    {{/if}}
  </template>
}
